import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useMyTemplatesFilter, useTaxLetterMutation, useTaxLetterBatchMutation } from '../../../../../queries';
import { ITemplateCard, Industry } from '../../../../../viewmodels/AppViewModels';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from '../../../../components-v2/Combobox';
import { CloseButton } from '../../../../components/CloseButton';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../components/HoverCard';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { Modal } from '../../../../components/Modal';
import { white } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { styleSheet } from './styles';

/** Split batch HTML into individual letters by page-break divs */
const splitLetters = (html: string): string[] => {
	const parts = html.split(/<div\s+style\s*=\s*["']page-break-after:\s*always;?["']\s*>\s*<\/div>/i);
	return parts.map(p => p.trim()).filter(Boolean);
};

/** Score a template for sorting: higher = more relevant */
const templateSortScore = (template: ITemplateCard): number => {
	const name = template.name?.toLowerCase() ?? '';
	const hasTax = name.includes('tax');
	const hasDefault = name.includes('default');
	if (hasTax && hasDefault) return 3;
	if (hasTax) return 2;
	return 0;
};

interface TaxLetterModalProps {
	isOpen: boolean;
	onClose: () => void;
	mode: 'single' | 'bulk';
	contactId?: string;
	donationIds?: string[];
	contactIds?: string[];
}

type ModalStep = 'selecting' | 'previewing';

export const TaxLetterModal = ({ isOpen, onClose, mode, contactId, donationIds, contactIds }: TaxLetterModalProps) => {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('TaxLetterModal');
	const industry = userSession?.account?.additionalInfo?.industry ?? Industry.Insurance;

	/** Wrap a single letter's HTML in a full document for iframe rendering */
	const wrapSingleForPrint = (html: string) =>
		`<!DOCTYPE html>
<html>
<head>
<style>
@page { margin: 0; }
@media print {
	.letter { padding: 0.5in 0.75in; }
}
body {
	font-family: Arial, Helvetica, sans-serif;
	margin: 0;
	padding: 16px;
}
</style>
</head>
<body><div class="letter">${html}</div></body>
</html>`;

	/** Wrap multiple letters into a printable document — each letter gets its own padded page */
	const wrapBatchForPrint = (html: string) => {
		const letters = splitLetters(html);
		const body = letters
			.map((letter, i) => {
				const pageBreak = i < letters.length - 1 ? ' page-break-after: always;' : '';
				return `<div class="letter" style="${pageBreak}">${letter}</div>`;
			})
			.join('\n');
		return `<!DOCTYPE html>
<html>
<head>
<style>
@page { margin: 0; }
.letter {
	padding: 0.5in 0.75in;
}
body {
	font-family: Arial, Helvetica, sans-serif;
	margin: 0;
	padding: 0;
}
</style>
</head>
<body>${body}</body>
</html>`;
	};

	/** Wrap HTML for iframe display — picks single vs batch strategy */
	const wrapForPrint = (html: string, isBatch = false) =>
		isBatch ? wrapBatchForPrint(html) : wrapSingleForPrint(html);

	const [step, setStep] = React.useState<ModalStep>('selecting');
	const [selectedTemplate, setSelectedTemplate] = React.useState<ITemplateCard>(null);
	const [previewHtml, setPreviewHtml] = React.useState<string>(null);
	const [printHtml, setPrintHtml] = React.useState<string>(null);
	const [currentLetterIndex, setCurrentLetterIndex] = React.useState(0);
	const iframeRef = React.useRef<HTMLIFrameElement>(null);
	const printIframeRef = React.useRef<HTMLIFrameElement>(null);

	// Year filter — shown when no specific donationIds are provided (donor-level or bulk)
	const showYearFilter = !donationIds?.length;
	const currentYear = new Date().getFullYear();
	const yearOptions: (number | 'all')[] = ['all', currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
	const [selectedYear, setSelectedYear] = React.useState<number | 'all'>(currentYear);

	// Split batch HTML into individual letters for paging in preview
	const letterPages = React.useMemo(() => {
		if (!printHtml || mode === 'single') return [];
		return splitLetters(printHtml);
	}, [printHtml, mode]);

	const dateRange = React.useMemo(() => {
		if (selectedYear === 'all') return {};
		return {
			startDate: `${selectedYear}-01-01`,
			endDate: `${selectedYear}-12-31`,
		};
	}, [selectedYear]);

	// Fetch user's templates
	const myTemplatesQuery = useMyTemplatesFilter({
		industry,
	});

	// Sort templates: tax+default first, then tax, then rest
	const sortedTemplates = React.useMemo(() => {
		const templates = myTemplatesQuery.data ?? [];
		return [...templates].sort((a, b) => templateSortScore(b) - templateSortScore(a));
	}, [myTemplatesQuery.data]);

	const [previewError, setPreviewError] = React.useState<string>(null);

	// Single tax letter mutation (used for preview in both modes)
	const taxLetterMutation = useTaxLetterMutation({
		onError: error => {
			logApiError('TaxLetter-Error', error);
			const status = (error as { systemCode?: number })?.systemCode;
			if (status === 400) {
				setPreviewError('noDonations');
			} else {
				setPreviewError('error');
			}
		},
	});

	// Batch mutation (returns HTML with page breaks between donors)
	const taxLetterBatchMutation = useTaxLetterBatchMutation({
		onError: error => {
			logApiError('TaxLetterBatch-Error', error);
			const status = (error as { systemCode?: number })?.systemCode;
			if (status === 400) {
				setPreviewError('noDonations');
			} else {
				setPreviewError('error');
			}
		},
	});

	/** Load preview for a given template */
	const loadPreview = React.useCallback(
		(template: ITemplateCard, yearOverride?: { startDate?: string; endDate?: string }) => {
			setPreviewHtml(null);
			setPreviewError(null);

			const previewContactId = mode === 'single' ? contactId : contactIds?.[0];
			if (!previewContactId) {
				setPreviewError('noDonations');
				return;
			}

			const range = yearOverride ?? (showYearFilter ? dateRange : {});

			taxLetterMutation.mutate(
				{
					request: {
						templateId: template.id,
						contactId: previewContactId,
						donationIds: mode === 'single' ? donationIds : undefined,
						...range,
					},
				},
				{
					onSuccess: data => {
						setPreviewHtml(data.html);
					},
				}
			);
		},
		// taxLetterMutation.mutate is stable — do not include the mutation object itself
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[mode, contactId, contactIds, donationIds, showYearFilter, dateRange]
	);

	// Combobox input state
	const [templateInputValue, setTemplateInputValue] = React.useState('');

	// Auto-select the first template when the list loads
	const hasAutoSelected = React.useRef(false);
	React.useEffect(() => {
		if (sortedTemplates.length > 0 && !hasAutoSelected.current) {
			hasAutoSelected.current = true;
			const first = sortedTemplates[0];
			setSelectedTemplate(first);
			setTemplateInputValue(first.name);
			loadPreview(first);
		}
	}, [sortedTemplates, loadPreview]);

	const onTemplateChanged = (value: unknown) => {
		const template = value as ITemplateCard;
		if (template) {
			setSelectedTemplate(template);
			setTemplateInputValue(template.name);
			loadPreview(template);
		}
	};

	const onGenerate = () => {
		if (!selectedTemplate) return;

		if (mode === 'single') {
			setPrintHtml(previewHtml);
			setStep('previewing');
		} else {
			setPrintHtml(null);
			setStep('previewing');
			taxLetterBatchMutation.mutate(
				{
					request: {
						templateId: selectedTemplate.id,
						contactIds,
						...dateRange,
					},
				},
				{
					onSuccess: data => {
						setPrintHtml(data.html);
					},
				}
			);
		}
	};

	const onPrint = () => {
		const frame = mode === 'bulk' && printHtml ? printIframeRef.current : iframeRef.current;
		if (frame?.contentWindow) {
			frame.contentWindow.print();
		}
	};

	const onYearChanged = (year: number | 'all') => {
		setSelectedYear(year);
		if (selectedTemplate) {
			const range = year === 'all' ? {} : { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
			loadPreview(selectedTemplate, range);
		}
	};

	const handleClose = () => {
		setStep('selecting');
		setSelectedTemplate(null);
		setTemplateInputValue('');
		setPreviewHtml(null);
		setPrintHtml(null);
		setPreviewError(null);
		setCurrentLetterIndex(0);
		setSelectedYear(currentYear);
		hasAutoSelected.current = false;
		onClose();
	};

	const renderPreviewPane = () => {
		if (!selectedTemplate) {
			return <div className={css(styleSheet.previewPlaceholder)}>Select a template to preview the tax letter</div>;
		}

		if (taxLetterMutation.isPending) {
			return (
				<div className={css(styleSheet.previewLoading)}>
					<LoadingSpinner type='small' />
				</div>
			);
		}

		if (previewError) {
			if (previewError === 'noDonations') {
				const yearLabel = selectedYear === 'all' ? 'the selected time period' : String(selectedYear);
				return (
					<div className={css(styleSheet.previewPlaceholder)}>
						No donations found for {yearLabel}. Try selecting a different year.
					</div>
				);
			}
			return (
				<div className={css(styleSheet.previewPlaceholder)}>
					Something went wrong generating the preview. Please try again.
				</div>
			);
		}

		if (previewHtml) {
			return (
				<iframe
					ref={iframeRef}
					srcDoc={wrapForPrint(previewHtml)}
					className={css(styleSheet.previewIframe)}
					title='Tax letter preview'
					sandbox='allow-same-origin allow-modals'
				/>
			);
		}

		return <div className={css(styleSheet.previewPlaceholder)}>Unable to load preview</div>;
	};

	const renderSelectingStep = () => (
		<>
			<div className={css(styleSheet.section)}>
				<div className={css(styleSheet.labelRow)}>
					<label className={css(styleSheet.label)}>Template</label>
					<HoverCard>
						<HoverCardTrigger className={css(styleSheet.helpLink)}>
							How to create a tax letter template
						</HoverCardTrigger>
						<HoverCardContent styles={[styleSheet.helpTooltip]}>
							<p>
								To create a new tax letter template, visit the Campaign section and click Create Email Campaign and type
								out your template. Click &quot;Save as&quot; and make sure to use the name &quot;Tax Letter&quot; in
								your template. Make sure to share with co-workers.
							</p>
							<p>
								If you&apos;d like this template to be the default tax letter, include the word &quot;Default&quot; in
								the name of the template.
							</p>
						</HoverCardContent>
					</HoverCard>
				</div>
				{myTemplatesQuery.isPending ? (
					<div className={css(styleSheet.previewLoading)}>
						<LoadingSpinner type='small' />
					</div>
				) : sortedTemplates.length === 0 ? (
					<div className={css(styleSheet.emptyState)}>
						No email templates found. Create a template in the Templates section first.
					</div>
				) : (
					<Combobox
						items={sortedTemplates}
						value={selectedTemplate ?? null}
						onValueChange={onTemplateChanged}
						inputValue={templateInputValue}
						onInputValueChange={setTemplateInputValue}
						itemToStringLabel={(t: unknown) => (t as ITemplateCard)?.name ?? ''}
						isItemEqualToValue={(a: unknown, b: unknown) => (a as ITemplateCard)?.id === (b as ITemplateCard)?.id}
					>
						<ComboboxInput placeholder='Search templates...' />
						<ComboboxContent>
							<ComboboxList>
								{(template: ITemplateCard) => (
									<ComboboxItem key={template.id} value={template}>
										{template.name}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
			</div>

			{showYearFilter ? (
				<div className={css(styleSheet.section)}>
					<label className={css(styleSheet.label)}>Donation Year</label>
					<div className={css(styleSheet.yearFilterContainer)}>
						{yearOptions.map(year => (
							<button
								key={year}
								className={css(styleSheet.yearButton, selectedYear === year && styleSheet.yearButtonActive)}
								onClick={() => onYearChanged(year)}
							>
								{year === 'all' ? 'All Time' : year}
							</button>
						))}
					</div>
				</div>
			) : null}

			<div className={css(styleSheet.section)}>
				<label className={css(styleSheet.label)}>Preview</label>
				<div className={css(styleSheet.previewContainer)}>{renderPreviewPane()}</div>
			</div>

			<div className={css(styleSheet.footer)}>
				<button
					className={css(baseStyleSheet.ctaButton)}
					onClick={onGenerate}
					disabled={!selectedTemplate || !previewHtml || taxLetterMutation.isPending}
				>
					{mode === 'single' ? 'Print Letter' : 'Print All Letters'}
				</button>
				<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={handleClose}>
					Cancel
				</button>
			</div>
		</>
	);

	const renderPreviewingStep = () => {
		const isLoading = mode === 'bulk' ? taxLetterBatchMutation.isPending : !printHtml;
		const batchFailed = mode === 'bulk' && !!previewError;
		const isBulkWithPages = mode === 'bulk' && letterPages.length > 1;
		const currentPageHtml = isBulkWithPages ? letterPages[currentLetterIndex] : printHtml;

		return (
			<>
				<div className={css(styleSheet.section)}>
					<label className={css(styleSheet.label)}>
						{mode === 'single' ? 'Tax Letter Preview' : 'Tax Letters Preview'}
					</label>

					{isBulkWithPages ? (
						<div className={css(styleSheet.paginationBar)}>
							<button
								className={css(styleSheet.paginationButton)}
								onClick={() => setCurrentLetterIndex(i => Math.max(0, i - 1))}
								disabled={currentLetterIndex === 0}
							>
								Previous
							</button>
							<span className={css(styleSheet.paginationText)}>
								Letter {currentLetterIndex + 1} of {letterPages.length}
							</span>
							<button
								className={css(styleSheet.paginationButton)}
								onClick={() => setCurrentLetterIndex(i => Math.min(letterPages.length - 1, i + 1))}
								disabled={currentLetterIndex === letterPages.length - 1}
							>
								Next
							</button>
						</div>
					) : null}

					<div className={css(styleSheet.previewContainer)}>
						{isLoading ? (
							<div className={css(styleSheet.previewLoading)}>
								<LoadingSpinner type='small' />
							</div>
						) : batchFailed ? (
							<div className={css(styleSheet.previewPlaceholder)}>
								{previewError === 'noDonations'
									? `No donations found for the selected donors${selectedYear !== 'all' ? ` in ${selectedYear}` : ''}. Try selecting a different year.`
									: 'Something went wrong generating the letters. Please try again.'}
							</div>
						) : currentPageHtml ? (
							<iframe
								ref={iframeRef}
								srcDoc={wrapForPrint(currentPageHtml)}
								className={css(styleSheet.previewIframe)}
								title='Tax letter preview'
								sandbox='allow-same-origin allow-modals'
							/>
						) : (
							<div className={css(styleSheet.previewPlaceholder)}>Unable to load letters</div>
						)}
					</div>
				</div>

				{/* Hidden iframe with all letters for printing */}
				{mode === 'bulk' && printHtml ? (
					<iframe
						ref={printIframeRef}
						srcDoc={wrapForPrint(printHtml, true)}
						style={{ display: 'none' }}
						title='Tax letters print'
						sandbox='allow-same-origin allow-modals'
					/>
				) : null}

				<div className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={onPrint} disabled={!printHtml}>
						{mode === 'single' ? 'Print' : `Print All (${letterPages.length})`}
					</button>
					<button
						className={css(baseStyleSheet.ctaButtonReverse)}
						onClick={() => {
							setStep('selecting');
							setPrintHtml(null);
							setCurrentLetterIndex(0);
							setPreviewError(null);
						}}
					>
						Back
					</button>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={handleClose}>
						Close
					</button>
				</div>
			</>
		);
	};

	return (
		<Modal
			className={css(styleSheet.modalContainer)}
			isOpen={isOpen}
			useDefaultHeader={true}
			onRequestClose={handleClose}
			headerAccessory={
				<div className={css(styleSheet.modalHeader)}>
					<CloseButton onClick={handleClose} fillColor={white} />
				</div>
			}
		>
			<article className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.header)}>{mode === 'single' ? 'Generate Tax Letter' : 'Generate Tax Letters'}</h3>
				{step === 'selecting' ? renderSelectingStep() : renderPreviewingStep()}
			</article>
		</Modal>
	);
};
